import { t } from '@lingui/core/macro';

import type { ConsentCategory } from './consent-signals';

/**
 * One source of copy for the consent categories, shared by the banner and the
 * account settings panel so the two can't drift.
 *
 * Values are thunks, not strings: `t` resolves against the active locale at
 * call time, and a module-level constant would freeze the copy at whichever
 * locale happened to be loaded first.
 *
 * This copy has not been through legal review (tracked separately).
 */
export const CONSENT_CATEGORY_COPY: Record<
  ConsentCategory,
  { label: () => string; hint: () => string }
> = {
  essential: {
    label: () => t`Essential`,
    hint: () =>
      t`Required for the site to work — signing in, security, and remembering your choices here. These cannot be turned off.`,
  },
  functional: {
    label: () => t`Functional`,
    hint: () =>
      t`Remember your preferences, such as language and layout, so the site behaves the way you left it.`,
  },
  analytics: {
    label: () => t`Analytics`,
    hint: () =>
      t`Measure visits and how pages are used, so we can find what is broken and improve it.`,
  },
  adMeasurement: {
    label: () => t`Ad measurement`,
    hint: () =>
      t`Measure how our ads perform, including whether a visit followed an ad. Does not personalize the ads you see.`,
  },
  adPersonalization: {
    label: () => t`Personalized advertising`,
    hint: () =>
      t`Use your activity to make the ads you see on other sites more relevant to you.`,
  },
};
